/**
 * Burnwood Studios sound: its own Web Audio graph, separate from the realm's Audio.ts.
 * - A score that is mixed by scroll position (muffled in the dungeon, opening up as you fall,
 *   swelling into the tunnel and the shatter).
 * - Wind that follows how fast you are falling.
 * - One-shot hits on timeline beats (forward only, so scrubbing back doesn't re-fire them).
 * Files live in public/audio/studio/ (licenses in public/audio/studio/LICENSES.md). A missing
 * file just stays silent.
 */
export type Hit = 'crack' | 'boom' | 'ignite' | 'glass' | 'confirm' | 'portal';

const MUSIC = 'music.mp3'; // streamed through an <audio> element, not decoded up front
const FILES: Record<Hit, string> = {
  crack: 'crack.ogg',
  boom: 'boom.ogg',
  ignite: 'ignite.ogg',
  glass: 'glass.wav',
  confirm: 'confirm.ogg',
  portal: 'portal.ogg',
};
const GAIN: Record<Hit, number> = { crack: 0.9, boom: 1, ignite: 0.75, glass: 0.85, confirm: 0.6, portal: 0.8 };

export class StudioAudio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicGain!: GainNode;
  private musicFilter!: BiquadFilterNode;
  private windGain!: GainNode;
  private windFilter!: BiquadFilterNode;
  private buffers: Partial<Record<Hit, AudioBuffer>> = {};
  private musicEl: HTMLAudioElement | null = null;
  private loading: Promise<void> | null = null;
  muted = false;
  private active = false;

  /** True once the browser lets us make sound (after a click, tap or key press). */
  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  /** Create the graph (safe to call before any gesture; it just stays suspended). */
  init(): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(ctx.destination);
    this.musicFilter = ctx.createBiquadFilter();
    this.musicFilter.type = 'lowpass';
    this.musicFilter.frequency.value = 400;
    this.musicGain = ctx.createGain();
    this.musicGain.gain.value = 0;
    this.musicFilter.connect(this.musicGain).connect(this.master);
    const el = new Audio(import.meta.env.BASE_URL + 'audio/studio/' + MUSIC);
    el.loop = true;
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    ctx.createMediaElementSource(el).connect(this.musicFilter);
    this.musicEl = el;
    // Wind: looped noise through a moving band-pass.
    const len = ctx.sampleRate * 3;
    const noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = noise.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) { last = last * 0.97 + (Math.random() * 2 - 1) * 0.03; d[i] = last * 6; }
    const wind = ctx.createBufferSource();
    wind.buffer = noise;
    wind.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.Q.value = 0.7;
    this.windFilter.frequency.value = 500;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    wind.connect(this.windFilter).connect(this.windGain).connect(this.master);
    wind.start();
    this.loading = this.load();
  }

  private async load(): Promise<void> {
    const base = import.meta.env.BASE_URL + 'audio/studio/';
    await Promise.all(Object.entries(FILES).map(async ([key, file]) => {
      try {
        const res = await fetch(base + file);
        if (!res.ok) return;
        this.buffers[key as Hit] = await this.ctx!.decodeAudioData(await res.arrayBuffer());
      } catch {
        // Missing or undecodable: that layer stays silent.
      }
    }));
  }

  /** Call from a user gesture (or when one has already happened) to start sound. */
  async resume(): Promise<boolean> {
    this.init();
    if (!this.ctx) return false;
    // Without a gesture, resume() can stay pending forever rather than reject: give it a moment.
    try { await Promise.race([this.ctx.resume(), new Promise((r) => setTimeout(r, 350))]); } catch { /* blocked until a gesture */ }
    if (!this.running) return false;
    this.active = true;
    await this.loading;
    this.master.gain.setTargetAtTime(this.muted ? 0 : 1, this.ctx.currentTime, 0.6);
    if (this.musicEl?.paused) void this.musicEl.play().catch(() => { /* retried on the next resume */ });
    return true;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.25);
  }

  /** Fade everything out (leaving the studio). */
  stop(): void {
    if (!this.ctx) return;
    this.active = false;
    this.master.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
    const el = this.musicEl;
    setTimeout(() => { if (el && !this.active) el.pause(); }, 2500);
  }

  /** Per-frame mix. `music` 0..1 level, `bright` 0..1 opens the filter, `fall` 0..1 wind. */
  mix(music: number, bright: number, fall: number): void {
    if (!this.ctx || !this.running) return;
    const t = this.ctx.currentTime;
    this.musicGain.gain.setTargetAtTime(music * 0.55, t, 0.25);
    this.musicFilter.frequency.setTargetAtTime(300 + bright * bright * 15000, t, 0.3);
    this.windGain.gain.setTargetAtTime(fall * 0.35, t, 0.15);
    this.windFilter.frequency.setTargetAtTime(350 + fall * 1400, t, 0.15);
  }

  hit(name: Hit, gain = 1): void {
    const buf = this.buffers[name];
    if (!this.ctx || !this.running || !buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    g.gain.value = GAIN[name] * gain;
    src.connect(g).connect(this.master);
    src.start();
  }
}
